<template>
  <div class="consult-request-page">
    <header class="console-page-head">
      <div class="console-page-copy">
        <h2 class="console-title">{{ pageTitle }}</h2>
      </div>
      <div class="console-head-side">
        <span class="console-count">{{ pagination.total }}건</span>
        <v-btn v-if="!lockedKind" variant="flat" class="btn-register" @click="nontaxModalOpen = true">
          <v-icon size="16" class="mr-1">mdi-chart-bar</v-icon>
          임금개선 월별 분석
        </v-btn>
      </div>
    </header>

    <div class="consult-request-body">
      <div class="content-card">
        <div class="consult-filter">
          <label class="consult-filter-search">
            <v-icon size="18">mdi-magnify</v-icon>
            <input
              v-model="keyword"
              type="text"
              placeholder="제목, 기관명"
              @keydown.enter="loadRequests(1)"
            />
            <button v-if="keyword" type="button" class="consult-filter-clear" @click="onClearSearch">지우기</button>
          </label>
          <div v-if="!lockedKind" class="consult-filter-status">
            <button
              v-for="opt in statusOptions"
              :key="opt.value || 'all'"
              type="button"
              :class="{ active: statusFilter === opt.value }"
              @click="setStatus(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
          <label v-if="!lockedKind" class="consult-filter-kind">
            <span>분류</span>
            <select v-model="kindFilter" @change="onKindChange">
              <option value="">전체</option>
              <option v-for="kind in kindOptions" :key="kind" :value="kind">{{ kind }}</option>
            </select>
          </label>
        </div>

        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :items-per-page="-1"
          hover
          class="consult-request-table"
          @click:row="onRowClick"
        >
          <template #item.index="{ index }">
            <span class="row-num">{{ (pagination.page - 1) * pagination.limit + index + 1 }}</span>
          </template>

          <template #item.status="{ item }">
            <span v-if="isApplicationKind(item)" class="consult-state is-new">접수</span>
            <span v-else class="consult-state" :class="item.status === 'DONE' ? 'is-done' : 'is-wait'">
              {{ item.status === 'DONE' ? '완료' : '대기' }}
            </span>
          </template>

          <template #item.kind="{ item }">
            <span class="consult-kind">{{ item.kind || '일반상담' }}</span>
          </template>

          <template #item.title="{ item }">
            <span class="consult-title-cell">
              <span class="consult-title-text">{{ item.title }}</span>
              <span v-if="item.hasExcel" class="consult-file">파일</span>
            </span>
          </template>

          <template #item.createdAt="{ item }">
            <span class="consult-date">{{ formatDateTime(item.createdAt) }}</span>
          </template>

          <template #no-data>
            <div class="empty-message">
              <p>{{ lockedKind ? '신청이 없습니다.' : '상담요청이 없습니다.' }}</p>
            </div>
          </template>

          <template #bottom>
            <TablePagination
              v-model:current-page="pagination.page"
              v-model:items-per-page="pagination.limit"
              :total-count="pagination.total"
              :total-pages="pagination.totalPages"
              @update:current-page="onPageChange"
              @update:items-per-page="onLimitChange"
            />
          </template>
        </v-data-table>
      </div>
    </div>

    <v-dialog v-model="detailOpen" max-width="760" scrollable>
      <v-card v-if="detail">
        <v-card-title class="detail-dialog-title">
          {{ lockedKind ? '설명회 신청 상세' : '상담요청 상세' }}
          <v-chip
            v-if="isDetailApplication"
            color="primary"
            size="small"
            variant="flat"
            class="ml-2"
          >
            접수
          </v-chip>
          <v-chip
            v-else
            :color="detail.request.status === 'DONE' ? 'success' : 'warning'"
            size="small"
            variant="flat"
            class="ml-2"
          >
            {{ detail.request.status === 'DONE' ? '답변완료' : '답변대기' }}
          </v-chip>
          <button
            type="button"
            class="detail-dialog-close"
            :disabled="submittingReply || deletingRequest"
            aria-label="닫기"
            @click="detailOpen = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>

        <v-card-text class="detail-dialog-body">
          <dl class="detail-info">
            <div>
              <dt>작성자 ID</dt>
              <dd>{{ detail.request.authorId }}</dd>
            </div>
            <div>
              <dt>분류</dt>
              <dd>{{ detail.request.kind || '일반상담' }}</dd>
            </div>
            <div>
              <dt>제목</dt>
              <dd>{{ detail.request.title }}</dd>
            </div>
            <div>
              <dt>기관명</dt>
              <dd>{{ detail.request.orgName }}</dd>
            </div>
            <div>
              <dt>기관기호</dt>
              <dd>{{ detail.request.orgCode || '—' }}</dd>
            </div>
            <div>
              <dt>연락처</dt>
              <dd>{{ detail.request.contactPhone }}</dd>
            </div>
            <div>
              <dt>이메일</dt>
              <dd>{{ detail.request.email || '—' }}</dd>
            </div>
            <div>
              <dt>접속 IP</dt>
              <dd>{{ detail.request.clientIp || '—' }}</dd>
            </div>
            <div>
              <dt>등록일</dt>
              <dd>{{ formatDateTime(detail.request.createdAt) }}</dd>
            </div>
          </dl>

          <div class="detail-content-block">
            <h3>상담 내용</h3>
            <p>{{ detail.request.content }}</p>
          </div>

          <div v-if="detail.request.hasExcel" class="detail-excel-block">
            <span>{{ detail.request.excelName }}</span>
            <v-btn
              size="small"
              variant="outlined"
              color="primary"
              :loading="downloadingId === detail.request.id"
              @click="downloadExcel(detail.request)"
            >
              Excel 다운로드
            </v-btn>
          </div>

          <div v-if="detail.responses.length" class="detail-responses">
            <h3>등록된 답변</h3>
            <article v-for="response in detail.responses" :key="response.id" class="response-item">
              <header>
                <strong>{{ response.byName || '관리자' }}</strong>
                <span>{{ formatDateTime(response.createdAt) }}</span>
              </header>
              <p>{{ response.content }}</p>
              <div v-if="response.hasExcel" class="response-excel">
                <span>{{ response.excelName }}</span>
                <v-btn
                  size="x-small"
                  variant="outlined"
                  color="primary"
                  :loading="downloadingResponseId === response.id"
                  @click="downloadResponseExcel(response)"
                >
                  Excel 다운로드
                </v-btn>
              </div>
            </article>
          </div>

          <div v-if="!isDetailApplication" class="detail-reply-form">
            <h3>답변 작성</h3>
            <div class="reply-suggest">
              <div class="reply-suggest__head">
                <span class="reply-suggest__label">
                  {{
                    aiSuggestSource === 'ai'
                      ? 'AI 답변 추천'
                      : aiSuggestLoading
                        ? 'AI 분석 중'
                        : '답변 추천'
                  }}
                </span>
                <v-btn
                  size="x-small"
                  variant="outlined"
                  :loading="aiSuggestLoading"
                  :disabled="!detail"
                  @click="loadAiSuggestions(true)"
                >
                  다시 생성
                </v-btn>
              </div>
              <p v-if="aiSuggestHint" class="reply-suggest__hint">{{ aiSuggestHint }}</p>
              <p v-if="aiSuggestLoading && !replySuggestions.length" class="reply-suggest__hint">
                문의 내용을 분석해 답변 초안을 만드는 중입니다…
              </p>
              <v-chip
                v-for="(item, index) in replySuggestions"
                :key="index"
                size="small"
                variant="outlined"
                class="reply-suggest__chip"
                @click="replyContent = item"
              >
                {{ item }}
              </v-chip>
            </div>
            <v-textarea
              v-model="replyContent"
              rows="6"
              variant="outlined"
              density="comfortable"
              hide-details
              placeholder="상담 답변 내용을 입력해 주세요."
            />
            <div class="reply-excel-upload">
              <span class="reply-excel-label">Excel 첨부 (선택)</span>
              <div class="reply-excel-control">
                <input
                  ref="replyExcelInput"
                  type="file"
                  class="reply-excel-input"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  @change="onReplyExcelChange"
                />
                <v-btn size="small" variant="outlined" @click="openReplyExcelPicker">
                  파일 선택
                </v-btn>
                <span class="reply-excel-name">
                  {{ replyExcelFile ? replyExcelFile.name : '선택된 파일 없음' }}
                </span>
                <v-btn
                  v-if="replyExcelFile"
                  size="small"
                  variant="text"
                  @click="clearReplyExcel"
                >
                  취소
                </v-btn>
              </div>
              <p class="reply-excel-hint">.xlsx, 5MB이하 · 매크로 포함 파일 불가</p>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="detail-dialog-actions">
          <v-btn
            v-if="isSuperAdmin"
            color="error"
            variant="text"
            :loading="deletingRequest"
            @click="deleteRequest"
          >
            삭제
          </v-btn>
          <v-spacer />
          <v-btn variant="text" @click="detailOpen = false">닫기</v-btn>
          <v-btn
            v-if="!isDetailApplication"
            color="primary"
            variant="flat"
            :loading="submittingReply"
            :disabled="!replyContent.trim()"
            @click="submitReply"
          >
            답변 등록
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConsultNontaxAnalysisModal v-model="nontaxModalOpen" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { consultBoardApi } from '@/services/api'
import TablePagination from '@/components/common/TablePagination.vue'
import ConsultNontaxAnalysisModal from '@/components/admin/ConsultNontaxAnalysisModal.vue'
import type {
  ConsultRequestDetailResponse,
  ConsultRequestListItem,
} from '@/types/consultBoard'

const props = defineProps<{
  lockedKind?: string
}>()

const lockedKind = computed(() => (props.lockedKind || '').trim())
const pageTitle = computed(() => (lockedKind.value ? '설명회신청관리' : '상담요청관리'))

// 설명회 신청처럼 답변이 필요 없는 접수성 분류인지 판별
const isApplicationKind = (item: { kind?: string | null; landingSlot?: string | null }) =>
  Boolean(item.landingSlot) || (item.kind || '').includes('설명회신청')

const headers = computed(() => {
  const columns = [
    { title: 'No', key: 'index', sortable: false, align: 'center' as const, width: '56px' },
    { title: '상태', key: 'status', sortable: false, width: '72px' },
    { title: '분류', key: 'kind', sortable: false, width: '180px' },
    { title: '제목', key: 'title', sortable: false },
    { title: '기관', key: 'orgName', sortable: false, width: '180px' },
    { title: '등록', key: 'createdAt', sortable: false, width: '140px' },
  ]
  if (!lockedKind.value) return columns
  return columns.filter((column) => column.key !== 'kind')
})

const statusOptions = [
  { label: '전체', value: '' },
  { label: '대기', value: 'WAIT' },
  { label: '완료', value: 'DONE' },
]

const replySuggestions = ref<string[]>([])
const aiSuggestLoading = ref(false)
const aiSuggestSource = ref<'ai' | 'fallback' | ''>('')
const aiSuggestHint = ref('')
let aiSuggestSeq = 0

const loading = ref(false)
const items = ref<ConsultRequestListItem[]>([])
const keyword = ref('')
const statusFilter = ref('')
const kindFilter = ref('')
const kindOptions = ref<string[]>([])
const pagination = ref({ page: 1, limit: 20, total: 0, totalPages: 1 })

const store = useStore()
const isSuperAdmin = computed(
  () => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0) >= 100,
)

const detailOpen = ref(false)
const detail = ref<ConsultRequestDetailResponse | null>(null)
const isDetailApplication = computed(() =>
  detail.value ? isApplicationKind(detail.value.request) : false,
)
const deletingRequest = ref(false)
const replyContent = ref('')
const replyExcelFile = ref<File | null>(null)
const replyExcelInput = ref<HTMLInputElement | null>(null)
const submittingReply = ref(false)
const downloadingId = ref<number | null>(null)
const downloadingResponseId = ref<number | null>(null)
const nontaxModalOpen = ref(false)

const formatDateTime = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const loadRequests = async (page = pagination.value.page) => {
  loading.value = true
  try {
    const res = await consultBoardApi.listRequests({
      page,
      limit: pagination.value.limit,
      keyword: keyword.value.trim(),
      status: statusFilter.value,
      kind: lockedKind.value || kindFilter.value,
    })
    items.value = res.data.data.items
    kindOptions.value = res.data.data.kinds || []
    pagination.value = res.data.data.pagination
  } catch (error) {
    console.error('상담요청 목록 로드 실패:', error)
    alert('상담요청 목록을 불러오지 못했습니다.')
  } finally {
    loading.value = false
  }
}

const onPageChange = () => {
  loadRequests(pagination.value.page)
}

const onLimitChange = () => {
  pagination.value.page = 1
  loadRequests(1)
}

const setStatus = (value: string) => {
  if (statusFilter.value === value) return
  statusFilter.value = value
  loadRequests(1)
}

const onKindChange = () => {
  pagination.value.page = 1
  loadRequests(1)
}

const onRowClick = (_event: Event, row: { item: ConsultRequestListItem }) => {
  openDetail(row.item.id)
}

const onClearSearch = () => {
  keyword.value = ''
  loadRequests(1)
}

const openDetail = async (id: number) => {
  try {
    const res = await consultBoardApi.getRequestDetail(id)
    detail.value = res.data.data
    replyContent.value = ''
    clearReplyExcel()
    detailOpen.value = true
    if (!isApplicationKind(detail.value.request)) {
      void loadAiSuggestions()
    }
  } catch (error) {
    console.error('상담요청 상세 로드 실패:', error)
    alert('상담요청 상세를 불러오지 못했습니다.')
  }
}

const deleteRequest = async () => {
  if (!detail.value || deletingRequest.value) return
  if (!isSuperAdmin.value) {
    alert('최고관리자만 삭제할 수 있습니다.')
    return
  }
  if (!confirm('이 상담요청을 삭제할까요? 삭제하면 목록에서 사라집니다.')) return

  deletingRequest.value = true
  try {
    await consultBoardApi.deleteRequest(detail.value.request.id)
    detailOpen.value = false
    detail.value = null
    await loadRequests()
  } catch (error: any) {
    const msg = error.response?.data?.message || '삭제에 실패했습니다.'
    alert(msg)
  } finally {
    deletingRequest.value = false
  }
}

const loadAiSuggestions = async (force = false) => {
  if (!detail.value) return
  if (aiSuggestLoading.value && !force) return

  const seq = ++aiSuggestSeq
  const requestId = detail.value.request.id
  aiSuggestLoading.value = true
  aiSuggestHint.value = ''
  if (force) replySuggestions.value = []

  try {
    const res = await consultBoardApi.suggestAiReplies(requestId)
    if (seq !== aiSuggestSeq) return
    const data = res.data.data as {
      suggestions: string[]
      source: 'ai' | 'fallback'
      message?: string | null
    }
    replySuggestions.value = data.suggestions || []
    aiSuggestSource.value = data.source
    aiSuggestHint.value =
      data.source === 'ai'
        ? '문의 내용을 분석한 AI 초안입니다. 클릭하면 입력란에 들어갑니다.'
        : data.message || '자동 초안입니다. OpenAI API 키를 설정하면 AI 추천이 활성화됩니다.'
  } catch (error) {
    if (seq !== aiSuggestSeq) return
    console.error('AI 답변 추천 실패:', error)
    replySuggestions.value = ['기능 오류 수정 완료입니다. 이용에 불편을 드려 죄송합니다.']
    aiSuggestSource.value = 'fallback'
    aiSuggestHint.value = 'AI 추천을 불러오지 못했습니다.'
  } finally {
    if (seq === aiSuggestSeq) aiSuggestLoading.value = false
  }
}

const openReplyExcelPicker = () => {
  replyExcelInput.value?.click()
}

const assignReplyExcel = (file: File | null) => {
  if (!file) {
    replyExcelFile.value = null
    return
  }
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    alert('Excel(.xlsx) 파일만 업로드할 수 있습니다.')
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    alert('파일 크기는 5MB이하여야 합니다.')
    return
  }
  replyExcelFile.value = file
}

const onReplyExcelChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  assignReplyExcel(target.files?.[0] || null)
}

const clearReplyExcel = () => {
  replyExcelFile.value = null
  if (replyExcelInput.value) {
    replyExcelInput.value.value = ''
  }
}

const submitReply = async () => {
  if (!detail.value || !replyContent.value.trim()) return

  submittingReply.value = true
  try {
    await consultBoardApi.createResponse(
      detail.value.request.id,
      replyContent.value.trim(),
      replyExcelFile.value,
    )
    alert('답변이 등록되었습니다.')
    await openDetail(detail.value.request.id)
    await loadRequests()
  } catch (error: any) {
    const msg = error.response?.data?.message || '답변 등록에 실패했습니다.'
    alert(msg)
  } finally {
    submittingReply.value = false
  }
}

const downloadExcel = async (item: { id: number; excelName?: string | null }) => {
  downloadingId.value = item.id
  try {
    await consultBoardApi.downloadExcel(item.id, item.excelName || `consult-${item.id}.xlsx`)
  } catch (error) {
    console.error('Excel 다운로드 실패:', error)
    alert('Excel 다운로드에 실패했습니다.')
  } finally {
    downloadingId.value = null
  }
}

const downloadResponseExcel = async (response: { id: number; excelName?: string | null }) => {
  if (!detail.value) return

  downloadingResponseId.value = response.id
  try {
    await consultBoardApi.downloadResponseExcel(
      detail.value.request.id,
      response.id,
      response.excelName || `consult-response-${response.id}.xlsx`,
    )
  } catch (error) {
    console.error('답변 Excel 다운로드 실패:', error)
    alert('Excel 다운로드에 실패했습니다.')
  } finally {
    downloadingResponseId.value = null
  }
}

onMounted(() => {
  loadRequests(1)
})
</script>
